import { useLayoutEffect, useRef } from 'react';
import { useT } from '../lib/i18n';
import { fmtDayNum, fmtWeekday, isToday, toCoachDateKey } from '../lib/time';

interface Props {
  days: Date[];
  selected: string;
  emptyDays: Set<string>;
  /** How many times are picked on each day, shown as a badge so picks on other days are not forgotten. */
  pickedPerDay?: Map<string, number>;
  onSelect: (key: string) => void;
  /**
   * Keep the selected day in view, second from the left, so one earlier
   * day peeks in and the strip reads as going back as well as forward.
   * Only when it is out of view: a tap on a visible day leaves the strip
   * where the thumb put it.
   */
  followSelected?: boolean;
  /** Days with free times get a dot under the date (the player's Book tab). */
  dotDays?: Set<string>;
  /** Days with a 🔥 time (the coach's calendar). */
  fireDays?: Set<string>;
}

/**
 * Days that have nothing open are dimmed rather than removed. A client who
 * sees Sunday missing entirely wonders whether the app is broken; a client
 * who sees Sunday greyed out learns the coach doesn't work Sundays.
 *
 * Days before today are muted, so a look back never reads as a day ahead.
 */
export function DayStrip({ days, selected, emptyDays, pickedPerDay, onSelect, followSelected, dotDays, fireDays }: Props) {
  const t = useT();
  const strip = useRef<HTMLDivElement>(null);
  const todayKey = toCoachDateKey(new Date());

  useLayoutEffect(() => {
    const el = strip.current;
    const pill = el?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!followSelected || !el || !pill) return;
    const left = pill.offsetLeft - el.offsetLeft;
    const inView = left >= el.scrollLeft && left + pill.offsetWidth <= el.scrollLeft + el.clientWidth;
    if (!inView) el.scrollLeft = left - pill.offsetWidth;
  }, [followSelected, selected, days]);

  return (
    <div className="daystrip" role="tablist" aria-label={t('a11y.chooseDay')} ref={strip}>
      {days.map((d) => {
        const key = toCoachDateKey(d);
        const empty = emptyDays.has(key);
        const picked = pickedPerDay?.get(key) ?? 0;
        return (
          <button
            key={key}
            role="tab"
            className={`day${isToday(d) ? ' day--today' : ''}${key < todayKey ? ' day--past' : ''}${empty ? ' day--empty' : ''}`}
            aria-selected={key === selected}
            onClick={() => onSelect(key)}
          >
            <span className="day__name">{isToday(d) ? t('day.today') : fmtWeekday(d)}</span>
            <span className="day__num">{fmtDayNum(d)}</span>
            {picked > 0
              ? <span className="day__badge">{picked}</span>
              : fireDays?.has(key) ? <span className="day__fire" aria-hidden="true">🔥</span>
              : dotDays?.has(key) && <span className="day__dot" aria-hidden="true" />}
          </button>
        );
      })}
    </div>
  );
}
